import React, { useMemo, useState } from 'react';
import { currencySymbol } from '../../utils/currency';
import './ForecastBreakdown.css';

const todayIso = () => new Date().toISOString().slice(0, 10);

const normalizeIsoDate = (value) => {
  if (!value) return todayIso();
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (typeof value === 'string') return value.slice(0, 10);
  return todayIso();
};

const rowIsoDate = (row) =>
  normalizeIsoDate(row?.effectiveDate || row?.businessDate || row?.date);

const dateDiffDays = (startIso, endIso) => {
  const start = new Date(`${startIso}T12:00:00`);
  const end = new Date(`${endIso}T12:00:00`);
  return Math.round((end - start) / 86_400_000);
};

const makeSelectionSet = (selectedLocations = ['all']) => (
  !selectedLocations.includes('all') && selectedLocations.length > 0
    ? new Set(selectedLocations.map((id) => parseInt(id, 10)).filter(Number.isFinite))
    : null
);

const forecastRevenueForRow = (row) =>
  Number(row?.otbForecast) ||
  Number(row?.rmsForecast) ||
  Number(row?.revparProjection) ||
  Number(row?.roomRevenue) ||
  0;

const otbRoomsForRow = (row) => {
  const otbRooms = Number(row?.otbRooms);
  if (Number.isFinite(otbRooms) && otbRooms !== 0) return otbRooms;
  return Number(row?.occupiedRooms) || 0;
};

const otbAvailableRoomsForRow = (row) => {
  const otbAvailableRooms = Number(row?.otbAvailableRooms);
  if (Number.isFinite(otbAvailableRooms) && otbAvailableRooms !== 0) return otbAvailableRooms;
  return Number(row?.availableRooms) || 0;
};

const nullableNumber = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const isPickupCurrentRow = (row) => row?.pickupDataCurrent === true || row?.pickupDataCurrent === 1;
const isPickupStaleRow = (row) => row?.pickupDataCurrent === false || row?.pickupDataCurrent === 0;
const isPickupUsableRow = (row) => isPickupCurrentRow(row) || (!isPickupCurrentRow(row) && !isPickupStaleRow(row));

const addNullable = (target, key, value) => {
  const n = nullableNumber(value);
  if (n === null) return;
  target[key] = target[key] === null ? n : target[key] + n;
};

const updateLatestForecastReportDate = (target, row) => {
  const reportDate = row?.latestForecastReportDate
    ? normalizeIsoDate(row.latestForecastReportDate)
    : null;
  if (!reportDate) return;
  if (!target.latestForecastReportDate || reportDate > target.latestForecastReportDate) {
    target.latestForecastReportDate = reportDate;
  }
};

const formatDateLabel = (iso) => {
  if (!iso) return 'older file';
  const date = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(iso).slice(0, 10);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Build per-property pickup-and-pace rows from forecastingData.byDate.
function buildRows(byDate, selectedLocations, locationNames, getLocationDisplayName, referenceDate) {
  if (!Array.isArray(byDate) || byDate.length === 0) return [];
  const sel = makeSelectionSet(selectedLocations);
  const anchorDate = normalizeIsoDate(referenceDate);

  const byLoc = new Map();
  byDate.forEach((r) => {
    const date = rowIsoDate(r);
    if (!date || date < anchorDate) return;
    if (r.locationId == null) return;
    if (sel && !sel.has(r.locationId)) return;
    if (!byLoc.has(r.locationId)) {
      byLoc.set(r.locationId, {
        locationId: r.locationId,
        tonightOtb: 0,
        tonightAvailable: 0,
        tonightRevenue: 0,
        p1: null,
        p1Rev: null,
        p7: null,
        p7Rev: null,
        p30: null,
        p30Rev: null,
        fcst30Rev: 0,
        nights: 0,
        latestForecastReportDate: null,
        hasCurrentPickupData: false,
        hasStalePickupData: false,
      });
    }
    const b = byLoc.get(r.locationId);
    const offset = dateDiffDays(anchorDate, date);

    updateLatestForecastReportDate(b, r);
    if (isPickupCurrentRow(r)) b.hasCurrentPickupData = true;
    if (isPickupStaleRow(r)) b.hasStalePickupData = true;

    if (offset === 0) {
      b.tonightOtb += otbRoomsForRow(r);
      b.tonightAvailable += otbAvailableRoomsForRow(r);
      b.tonightRevenue += forecastRevenueForRow(r);
      if (isPickupUsableRow(r)) {
        addNullable(b, 'p1', r?.pickupRooms1Day);
        addNullable(b, 'p1Rev', r?.pickupRevenue1Day);
      }
    }
    if (offset >= 0 && offset < 7 && isPickupUsableRow(r)) {
      addNullable(b, 'p7', r?.pickupRooms1Day);
      addNullable(b, 'p7Rev', r?.pickupRevenue1Day);
    }
    if (offset >= 0 && offset < 30 && isPickupUsableRow(r)) {
      addNullable(b, 'p30', r?.pickupRooms1Day);
      addNullable(b, 'p30Rev', r?.pickupRevenue1Day);
    }
    if (offset >= 0 && offset < 30) {
      b.fcst30Rev += forecastRevenueForRow(r);
      b.nights += 1;
    }
  });

  return [...byLoc.values()].map((b) => {
    const name = getLocationDisplayName
      ? getLocationDisplayName(b.locationId, locationNames?.[b.locationId])
      : (locationNames?.[b.locationId] || `Location ${b.locationId}`);
    const occ = b.tonightAvailable > 0
      ? (b.tonightOtb / b.tonightAvailable) * 100
      : 0;
    let tone = 'neu';
    if (occ >= 80) tone = 'pos';
    else if (occ > 0 && occ < 50) tone = 'warn';
    else if (occ === 0) tone = 'neu';
    else tone = 'pos';
    return {
      id: b.locationId,
      name,
      flag: getInitials(name),
      tone,
      cap: b.tonightAvailable,
      otb: b.tonightOtb,
      otbRevenue: b.tonightRevenue,
      occ,
      p1: b.p1,
      p1Rev: b.p1Rev,
      p7: b.p7,
      p7Rev: b.p7Rev,
      p30: b.p30,
      p30Rev: b.p30Rev,
      fcst30: b.fcst30Rev,
      latestForecastReportDate: b.latestForecastReportDate,
      pickupDataCurrent: b.hasCurrentPickupData || !b.hasStalePickupData,
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
}

function getInitials(name) {
  if (!name || typeof name !== 'string') return '—';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

const fmtCompactCurrency = (v, currency) => {
  const n = Number(v) || 0;
  const sym = currencySymbol(currency);
  if (Math.abs(n) >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}K`;
  return `${sym}${Math.round(n).toLocaleString()}`;
};

function PickupCell({ value, revenue, row, currency }) {
  if (value == null) {
    return (
      <>
        <div className="fp-val muted">—</div>
        {row?.pickupDataCurrent === false && (
          <div className="fp-sub-text">Forecast file {formatDateLabel(row.latestForecastReportDate)}</div>
        )}
      </>
    );
  }
  const tone = value > 0 ? 'pos' : value < 0 ? 'neg' : 'neu';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return (
    <>
      <div className={'fp-val fp-pickup tone-' + tone}>
        {sign}{Math.abs(Math.round(value)).toLocaleString()}
      </div>
      {Number(revenue) !== 0 && (
        <div className="fp-sub-text">{fmtCompactCurrency(revenue, currency)}</div>
      )}
    </>
  );
}

function ForecastBreakdown({
  forecastingData,
  dashboardData,
  locationNames,
  selectedLocations = ['all'],
  getLocationDisplayName,
  referenceDate,
  currency,
}) {
  const rows = useMemo(
    () => buildRows(forecastingData?.byDate, selectedLocations, locationNames, getLocationDisplayName, referenceDate),
    [forecastingData, selectedLocations, locationNames, getLocationDisplayName, referenceDate]
  );

  const [sort, setSort] = useState({ key: 'fcst30', dir: 'desc' });
  const onSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return null;
    });
  };

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const an = typeof av === 'number' ? av : Number.NEGATIVE_INFINITY;
      const bn = typeof bv === 'number' ? bv : Number.NEGATIVE_INFINITY;
      return sort.dir === 'desc' ? bn - an : an - bn;
    });
    return copy;
  }, [rows, sort]);

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      acc.cap += r.cap || 0;
      acc.otb += r.otb || 0;
      acc.otbRevenue += r.otbRevenue || 0;
      addNullable(acc, 'p1', r.p1);
      addNullable(acc, 'p1Rev', r.p1Rev);
      addNullable(acc, 'p7', r.p7);
      addNullable(acc, 'p7Rev', r.p7Rev);
      addNullable(acc, 'p30', r.p30);
      addNullable(acc, 'p30Rev', r.p30Rev);
      acc.fcst30 += r.fcst30 || 0;
      return acc;
    }, { cap: 0, otb: 0, otbRevenue: 0, p1: null, p1Rev: null, p7: null, p7Rev: null, p30: null, p30Rev: null, fcst30: 0 });
  }, [rows]);

  const cols = [
    { key: 'occ',    label: 'Tonight Occ %', sortable: true },
    { key: 'otb',    label: 'OTB Tonight',   sortable: true },
    { key: 'p1',     label: 'Pickup Tonight', sortable: false },
    { key: 'p7',     label: 'Pickup Next 7 Nights', sortable: false },
    { key: 'p30',    label: 'Pickup Next 30 Nights', sortable: false },
    { key: 'fcst30', label: '30d Fcst Rev',  sortable: true },
  ];

  if (rows.length === 0) {
    return (
      <div className="fp-card">
        <div className="fp-head">
          <div>
            <h2 className="fp-title">Pickup by property</h2>
            <div className="fp-sub">Pickup is summed by stay date: tonight, next 7 nights, and next 30 nights.</div>
          </div>
        </div>
        <div className="fp-empty">No forward-looking forecast data for the current selection.</div>
      </div>
    );
  }

  return (
    <div className="fp-card">
      <div className="fp-head">
        <div>
          <h2 className="fp-title">Pickup by property</h2>
          <div className="fp-sub">
            Pickup is summed by stay date: tonight, next 7 nights, and next 30 nights.
          </div>
        </div>
      </div>

      <div className="fp-scroll">
        <table className="fp-table">
          <thead>
            <tr>
              <th className="first">Property</th>
              {cols.map((c) => {
                const isSorted = sort?.key === c.key;
                return (
                  <th
                    key={c.key}
                    className={(c.sortable ? 'sortable' : '') + (isSorted ? ' sorted' : '')}
                    onClick={c.sortable ? () => onSort(c.key) : undefined}
                  >
                    {c.label}
                    {c.sortable && (
                      <span className="fp-sort">
                        {isSorted ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr key={r.id} className={'fp-row tone-' + (r.tone || 'neu')}>
                <td className="first">
                  <div className="fp-name-cell">
                    <span className={'fp-flag tone-' + (r.tone || 'neu')}>{r.flag}</span>
                    <span className="fp-name">{r.name}</span>
                  </div>
                </td>
                <td>
                  <div className="fp-val">{r.occ.toFixed(1)}%</div>
                  <div className="fp-occ-bar">
                    <span style={{ width: `${Math.min(100, Math.max(0, r.occ))}%` }} />
                  </div>
                </td>
                <td>
                  <div className="fp-val">{Math.round(r.otb).toLocaleString()}</div>
                  <div className="fp-sub-text">
                    {r.cap > 0 ? `of ${Math.round(r.cap).toLocaleString()} · ` : ''}
                    {fmtCompactCurrency(r.otbRevenue, currency)}
                  </div>
                </td>
                <td><PickupCell value={r.p1} revenue={r.p1Rev} row={r} currency={currency} /></td>
                <td><PickupCell value={r.p7} revenue={r.p7Rev} row={r} currency={currency} /></td>
                <td><PickupCell value={r.p30} revenue={r.p30Rev} row={r} currency={currency} /></td>
                <td><div className="fp-val">{fmtCompactCurrency(r.fcst30, currency)}</div></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="first">Portfolio</td>
              <td>
                <div className="fp-val strong">
                  {totals.cap > 0 ? ((totals.otb / totals.cap) * 100).toFixed(1) : '0.0'}%
                </div>
              </td>
              <td>
                <div className="fp-val strong">{Math.round(totals.otb).toLocaleString()}</div>
                <div className="fp-sub-text">
                  {totals.cap > 0 ? `of ${Math.round(totals.cap).toLocaleString()} · ` : ''}
                  {fmtCompactCurrency(totals.otbRevenue, currency)}
                </div>
              </td>
              <td><PickupCell value={totals.p1} revenue={totals.p1Rev} currency={currency} /></td>
              <td><PickupCell value={totals.p7} revenue={totals.p7Rev} currency={currency} /></td>
              <td><PickupCell value={totals.p30} revenue={totals.p30Rev} currency={currency} /></td>
              <td><div className="fp-val strong">{fmtCompactCurrency(totals.fcst30, currency)}</div></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default ForecastBreakdown;
export { ForecastBreakdown };
